import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Globe, Calculator, DollarSign } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './VisaFeeCalculatorPage/components/CalculationForm';
import ResultsOverview from './VisaFeeCalculatorPage/components/ResultsOverview';
import FeeBreakdown from './VisaFeeCalculatorPage/components/FeeBreakdown';
import QuickSelectionSidebar from './VisaFeeCalculatorPage/components/QuickSelectionSidebar';
import ComparisonSidebar from './VisaFeeCalculatorPage/components/ComparisonSidebar';
import InfoContent from './VisaFeeCalculatorPage/components/InfoContent';
import VisaRequirementsSidebar from './VisaFeeCalculatorPage/components/VisaRequirementsSidebar';

// Определение начального состояния формы
export const initialFormData = {
  country: 'schengen',
  visaType: 'tourist',
  visaCategory: 'standard',
  entryType: 'single',
  processingTime: 'standard',
  visaDuration: '90', // Дни
  applicantType: 'adult',
  applicantsCount: '1',
  includeServiceFee: false,
  includeVAC: true, // Визовый сервисный центр
  includeInsurance: false,
  includeTranslation: false,
  includeDelivery: false,
  insuredDays: '30',
  insuranceCoverage: 'standard',
  documentCount: '5',
  deliveryType: 'standard',
  paymentCurrency: 'rub'
};

// Определение типов виз
export const visaTypes = [
  { id: 'tourist', name: 'Туристическая', defaultFee: 35, isPopular: true },
  { id: 'business', name: 'Деловая', defaultFee: 35, isPopular: true },
  { id: 'work', name: 'Рабочая', defaultFee: 80, isPopular: true },
  { id: 'student', name: 'Студенческая/учебная', defaultFee: 35, isPopular: true },
  { id: 'transit', name: 'Транзитная', defaultFee: 25, isPopular: false },
  { id: 'medical', name: 'Медицинская', defaultFee: 35, isPopular: false },
  { id: 'cultural', name: 'Культурная/спортивная', defaultFee: 35, isPopular: false },
  { id: 'family', name: 'Воссоединение семьи', defaultFee: 60, isPopular: false },
  { id: 'diplomatic', name: 'Дипломатическая', defaultFee: 0, isPopular: false },
  { id: 'official', name: 'Служебная', defaultFee: 0, isPopular: false }
];

// Определение стран для получения визы
export const countries = [
  { id: 'schengen', name: 'Шенген (общий)', category: 'europe', defaultFee: 80, isPopular: true },
  { id: 'usa', name: 'США', category: 'americas', defaultFee: 160, isPopular: true },
  { id: 'uk', name: 'Великобритания', category: 'europe', defaultFee: 120, isPopular: true },
  { id: 'canada', name: 'Канада', category: 'americas', defaultFee: 100, isPopular: true },
  { id: 'australia', name: 'Австралия', category: 'asia_pacific', defaultFee: 145, isPopular: true },
  { id: 'china', name: 'Китай', category: 'asia_pacific', defaultFee: 65, isPopular: true },
  { id: 'japan', name: 'Япония', category: 'asia_pacific', defaultFee: 55, isPopular: true },
  { id: 'india', name: 'Индия', category: 'asia_pacific', defaultFee: 85, isPopular: false },
  { id: 'uae', name: 'ОАЭ', category: 'middle_east', defaultFee: 90, isPopular: false },
  { id: 'turkey', name: 'Турция', category: 'middle_east', defaultFee: 25, isPopular: true },
  { id: 'thailand', name: 'Таиланд', category: 'asia_pacific', defaultFee: 40, isPopular: true },
  { id: 'south_korea', name: 'Южная Корея', category: 'asia_pacific', defaultFee: 50, isPopular: false },
  { id: 'egypt', name: 'Египет', category: 'africa', defaultFee: 25, isPopular: true },
  { id: 'germany', name: 'Германия', category: 'europe', defaultFee: 80, isPopular: false },
  { id: 'france', name: 'Франция', category: 'europe', defaultFee: 80, isPopular: false },
  { id: 'italy', name: 'Италия', category: 'europe', defaultFee: 80, isPopular: false },
  { id: 'spain', name: 'Испания', category: 'europe', defaultFee: 80, isPopular: false },
  { id: 'finland', name: 'Финляндия', category: 'europe', defaultFee: 80, isPopular: false },
  { id: 'greece', name: 'Греция', category: 'europe', defaultFee: 80, isPopular: false },
  { id: 'czech', name: 'Чехия', category: 'europe', defaultFee: 80, isPopular: false },
  { id: 'poland', name: 'Польша', category: 'europe', defaultFee: 80, isPopular: false },
  { id: 'malaysia', name: 'Малайзия', category: 'asia_pacific', defaultFee: 35, isPopular: false },
  { id: 'vietnam', name: 'Вьетнам', category: 'asia_pacific', defaultFee: 50, isPopular: false },
  { id: 'indonesia', name: 'Индонезия', category: 'asia_pacific', defaultFee: 45, isPopular: false },
  { id: 'brazil', name: 'Бразилия', category: 'americas', defaultFee: 80, isPopular: false },
  { id: 'mexico', name: 'Мексика', category: 'americas', defaultFee: 55, isPopular: false },
  { id: 'new_zealand', name: 'Новая Зеландия', category: 'asia_pacific', defaultFee: 140, isPopular: false },
  { id: 'israel', name: 'Израиль', category: 'middle_east', defaultFee: 0, isPopular: false },
  { id: 'singapore', name: 'Сингапур', category: 'asia_pacific', defaultFee: 30, isPopular: false },
  { id: 'qatar', name: 'Катар', category: 'middle_east', defaultFee: 95, isPopular: false },
  { id: 'south_africa', name: 'ЮАР', category: 'africa', defaultFee: 70, isPopular: false }
];

// Определение категорий стран для группировки
export const countryCategories = [
  { id: 'europe', name: 'Европа' },
  { id: 'asia_pacific', name: 'Азия и Океания' },
  { id: 'americas', name: 'Америка' },
  { id: 'middle_east', name: 'Ближний Восток' },
  { id: 'africa', name: 'Африка' }
];

// Определение типов обработки визы
export const processingTimes = [
  { id: 'standard', name: 'Стандартная (7-15 дней)', multiplier: 1.0 },
  { id: 'express', name: 'Ускоренная (3-5 дней)', multiplier: 1.5 },
  { id: 'urgent', name: 'Срочная (1-2 дня)', multiplier: 2.0 },
  { id: 'vip', name: 'VIP обработка (в день подачи)', multiplier: 3.0 }
];

// Сервисные сборы ВЦ по странам (в долларах)
export const vacServiceFees = {
  schengen: 35,
  usa: 38,
  uk: 40,
  canada: 35,
  australia: 45,
  china: 25,
  japan: 25,
  india: 30,
  uae: 35,
  turkey: 25,
  thailand: 30,
  default: 30
};

/**
 * VisaFeeCalculatorPage Component - Полный калькулятор визовых сборов с учетом различных стран и типов виз.
 */
const VisaFeeCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('visa-fee-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор визового сбора | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте полную стоимость визы с учетом консульского сбора, сервисных платежей, страховки и дополнительных услуг для граждан России. Точный расчет стоимости виз для Шенгена, США, Великобритании и других стран.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [feeBreakdown, setFeeBreakdown] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [currentExchangeRates, setCurrentExchangeRates] = useState({
    USD: 92.5,
    EUR: 101.2,
    GBP: 118.4,
    CNY: 12.8,
    JPY: 0.62,
    INR: 1.1,
    AUD: 61.5,
    CAD: 68.3,
    AED: 25.2,
    THB: 2.6
  });

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedData = localStorage.getItem('savedVisaCalculations');
    if (savedData) {
      try {
        setSavedCalculations(JSON.parse(savedData));
      } catch (e) {
        console.error("Error loading saved visa calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'schengen':
          newState.country = 'schengen';
          newState.visaType = 'tourist';
          break;
        case 'usa':
          newState.country = 'usa';
          newState.visaType = 'tourist';
          break;
        case 'uk':
          newState.country = 'uk';
          newState.visaType = 'tourist';
          break;
        case 'business':
          newState.visaType = 'business';
          break;
        case 'work':
          newState.visaType = 'work';
          break;
        case 'student':
          newState.visaType = 'student';
          break;
        case 'tourism':
          newState.visaType = 'tourist';
          break;
        case 'express':
          newState.processingTime = 'express';
          break;
        case 'family':
          newState.applicantsCount = '3';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setFeeBreakdown([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Виза ${getCountryName(formData.country)}, ${getVisaTypeName(formData.visaType)}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      country: formData.country,
      visaType: formData.visaType
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedVisaCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedVisaCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calculationToLoad = savedCalculations.find(calc => calc.id === id);
    if (calculationToLoad) {
      setFormData(calculationToLoad.formData);
      setResults(calculationToLoad.results);
      
      // Если есть детальная разбивка сборов в сохраненном расчете, используем ее
      if (calculationToLoad.results.feeBreakdown) {
        setFeeBreakdown(calculationToLoad.results.feeBreakdown);
      } else {
        // Иначе, заново генерируем разбивку на основе результатов
        generateFeeBreakdown(calculationToLoad.results);
      }
    }
  };

  // Получить название страны по ID
  const getCountryName = (countryId) => {
    const country = countries.find(c => c.id === countryId);
    return country ? country.name : 'Неизвестная страна';
  };

  // Получить название типа визы по ID
  const getVisaTypeName = (visaTypeId) => {
    const type = visaTypes.find(t => t.id === visaTypeId);
    return type ? type.name : 'Неизвестный тип визы';
  };

  // Генерировать детальную разбивку сборов
  const generateFeeBreakdown = (calculationResults) => {
    if (!calculationResults) return [];

    const breakdown = [
      {
        name: 'Консульский сбор',
        amount: calculationResults.consulateFee,
        currency: 'EUR',
        description: 'Официальный сбор консульства/посольства за рассмотрение визового заявления'
      }
    ];

    if (calculationResults.serviceFee > 0) {
      breakdown.push({
        name: 'Сервисный сбор визового центра',
        amount: calculationResults.serviceFee,
        currency: 'EUR',
        description: 'Плата за услуги визового центра (прием документов, биометрия и т.д.)'
      });
    }

    if (calculationResults.insuranceCost > 0) {
      breakdown.push({
        name: 'Туристическая страховка',
        amount: calculationResults.insuranceCost,
        currency: 'RUB',
        description: 'Обязательная медицинская страховка на период поездки'
      });
    }

    if (calculationResults.translationCost > 0) {
      breakdown.push({
        name: 'Перевод документов',
        amount: calculationResults.translationCost,
        currency: 'RUB',
        description: `Перевод ${formData.documentCount} документов с заверением`
      });
    }

    if (calculationResults.deliveryCost > 0) {
      breakdown.push({
        name: 'Доставка документов',
        amount: calculationResults.deliveryCost,
        currency: 'RUB',
        description: 'Доставка готовых документов курьерской службой'
      });
    }

    setFeeBreakdown(breakdown);
    return breakdown;
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Globe className="w-8 h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор визового сбора'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              feeBreakdown={feeBreakdown}
              setFeeBreakdown={setFeeBreakdown}
              countries={countries}
              countryCategories={countryCategories}
              visaTypes={visaTypes}
              processingTimes={processingTimes}
              vacServiceFees={vacServiceFees}
              exchangeRates={currentExchangeRates}
              saveCalculation={saveCalculation}
              generateFeeBreakdown={generateFeeBreakdown}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview 
                  results={results} 
                  formData={formData}
                  countries={countries}
                  visaTypes={visaTypes}
                  exchangeRates={currentExchangeRates}
                />
                
                <FeeBreakdown 
                  feeBreakdown={feeBreakdown}
                  results={results}
                  exchangeRates={currentExchangeRates}
                  preferredCurrency={formData.paymentCurrency}
                />
              </>
            )}

            {/* --- Информационный контент о визах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрого выбора визы --- */}
            <QuickSelectionSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              countries={countries}
              visaTypes={visaTypes}
            />

            {/* --- Панель требований для визы --- */}
            <VisaRequirementsSidebar 
              selectedCountry={formData.country}
              selectedVisaType={formData.visaType}
              countries={countries}
              visaTypes={visaTypes}
            />

            {/* --- Панель сравнения виз --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
              countries={countries}
              visaTypes={visaTypes}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Customized Radio Button for Visa Type */
        .visa-type-radio {
          @apply relative flex cursor-pointer rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 focus:outline-none;
        }
        .visa-type-radio-checked {
          @apply border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/20;
        }
        .visa-type-radio-label {
          @apply flex flex-col;
        }
        .visa-type-radio input[type="radio"] {
          @apply sr-only;
        }
      `}</style>
    </div>
  );
};

export default VisaFeeCalculatorPage;